<template>
  <div :class="{ 'd-none': hidden }">
    <AppLoading
      v-if="isLoading"
      style="height: 30vh"
    />
    <template v-else>
      <template v-if="useRequirementLevels && !diffEnabled">
        <div
          v-for="(level, index) in [...viewableRequirementLevels, ...noLevelObject]"
          :key="`level-${index}`"
          class="mb-2 mt-4"
        >
          <h6 v-if="level.key" class="font-weight-semi-bold">
            {{ getRequirementLevelLabel(level.key) }}:
          </h6>
          <h6 v-else-if="requirementsWithoutLevels.length > 0" class="font-weight-semi-bold">
            {{ '(No requirement level)' }}:
          </h6>
          <FormWysiwyg
            v-if="disabled && index < requirementLevels.length && level.config.editorEnabled"
            class="mb-2"
            :disabled="true"
            :value="getRequirementLevelNote(level.key)"
          />
          <FormWysiwyg
            v-else-if="index < requirementLevels.length && level.config.editorEnabled"
            class="mb-2"
            :value="getRequirementLevelNote(level.key)"
            :disabled="!isLevelEditable(level.key)"
            :config="{
              disableExtensions: ['embed', 'file']
            }"
            placeholder="Give some contexts to the requirements below"
            @input="updateRequirementLevelsData(level.key, 'notes', $event)"
          />
          <span
            v-if="localValue.filter(v => v.requirementLevel === level.key).length === 0 && level.key"
            class="text-muted font-italic"
          >
            No requirements yet
          </span>
          <AppSortable
            :value="localValue.filter(v => v.requirementLevel === level.key)"
            :disabled="disabled"
            keySuffix="rq"
            @input="v => updateWithPositioning(v, useRequirementLevels)"
          >
            <template #default="{ item: requisite, key: reqIndex }">
              <CommonAccordion
                class="rounded-5 mb-2"
                style="background-color:#FCFCFC"
                :title="!disabled && 'Drag to sort'"
              >
                <template #header>
                  <div class="d-flex w-100 p-3">
                    <AppIcon
                      v-if="!disabled"
                      name="drag_indicator"
                      class="mr-2"
                      style="opacity:0.5"
                    />
                    <span class="flex-1 text-left">{{ requisite.name || 'Untitled Requirement' }}</span>
                    <span>
                      <AppBadge v-if="requisite.showInCatalog">Shown in catalog</AppBadge>
                    </span>
                  </div>
                </template>
                <template #content>
                  <div
                    class="p-3"
                    :data-test="`simpleRequisiteContent${reqIndex}`"
                  >
                    <template v-if="disabled">
                      <AppButtonWithLoader
                        v-if="isScribeGenerateEnabled && isFieldVisible('editScribe')"
                        class="float-right"
                        :raised="true"
                        :loading="isScribeGenerating"
                        @click="generateScribe(requisite)"
                      >Convert to Scribe</AppButtonWithLoader>
                      <div v-if="requisite.requirementLevel && requisite.requirementLevel.toLowerCase() === 'subplan' && !isNarrativeText(requisite.type)">
                        <FormLabel label="Plan code" />
                        {{ requisite.planCode || 'No Plan Code Set' }}
                      </div>
                      <div>
                        <FormLabel label="Requirement level" />
                        {{ getRequirementLevelLabel(requisite.requirementLevel) }}
                      </div>
                      <div v-if="!isNarrativeText(requisite.type)">
                        <FormLabel label="Name" />
                        {{ requisite.name || 'No Name Set' }}
                      </div>
                      <div v-if="!isNarrativeText(requisite.type) && isFieldVisible('sisId')">
                        <FormLabel label="SIS ID" />
                        {{ requisite.sisId || 'No SIS ID Set' }}
                      </div>
                      <div v-if="!isNarrativeText(requisite.type)">
                        <FormLabel label="Type" />
                        {{ (requisite.type || 'noTypeSet') | decamelize }}
                      </div>
                      <div
                        v-for="(rule, ruleIndex) in requisite.rules || []"
                        :key="`rule-${ruleIndex}-${rule.id}`"
                      >
                        <SimpleRequisiteRuleDisplay
                          :rule="rule"
                          :root="true"
                          :depth="0"
                          class="card card-body"
                          :isRulesFieldVisible="isRulesFieldVisible"
                          :titleInterpolation="titleInterpolation"
                        />
                      </div>

                      <div v-if="isFieldVisible('additionalComments')">
                        <FormLabel
                          :id="`additionalComments${reqIndex}`"
                          label="Additional Comments"
                          class="pt-2"
                        />
                        <FormWysiwyg
                          v-if="requisite.notes"
                          :id="`additionalComments${reqIndex}`"
                          :disabled="true"
                          :value="requisite.notes"
                        />
                        <div
                          v-else-if="disabled"
                          class="text-muted"
                        >Not Set</div>
                      </div>
                    </template>
                    <template v-else>
                      <FormSelect
                        v-if="requisite.requirementLevel && requisite.requirementLevel.toLowerCase() === 'subplan' && !isNarrativeText(requisite.type)"
                        label="Plan code"
                        placeholder="Requirement plan code"
                        class="w-1/3"
                        :value="requisite.planCode"
                        :disabled="!isLevelEditable(level.key)"
                        :config="{options: specializationsOptions}"
                        @input="updateData(requisite.id, 'planCode', $event)"
                      />
                      <div class="d-flex w-100 mt-2">
                        <FormSelect
                          label="Requirement level"
                          placeholder="Requirement level"
                          class="flex-1"
                          :value="requisite.requirementLevel"
                          :disabled="!isLevelEditable(level.key)"
                          :config="{options: requirementLevelsTemplateOptions, trackBy: 'key'}"
                          :customLabel="level => level.label"
                          @input="updateData(requisite.id, 'requirementLevel', $event)"
                        />
                        <FormSelect
                          v-if="!isNarrativeText(requisite.type)"
                          label="Type"
                          placeholder="Requisite Type"
                          class="flex-1 ml-4"
                          dataTest="requirementType"
                          :value="requisite.type"
                          :disabled="!isLevelEditable(level.key) || !isFieldEditable('requirementType')"
                          :config="{options: filteredTypeOptions}"
                          @input="updateData(requisite.id, 'type', $event)"
                        />
                        <div v-if="isFieldVisible('showInCatalog')" class="flex-1 ml-4">
                          <FormLabel :id="`showInCatalog${reqIndex}`" label="Visibility" />
                          <AppSwitch
                            :id="`showInCatalog${reqIndex}`"
                            :value="(requisite.showInCatalog === undefined)?showInCatalogDefaultValue:requisite.showInCatalog"
                            :disabled="!isLevelEditable(level.key) || !isFieldEditable('showInCatalog')"
                            :label="`Show in ${$localize('catalog')}`"
                            class="pt-1"
                            @input="updateData(requisite.id, 'showInCatalog', $event)"
                          />
                        </div>
                      </div>
                      <FormText
                        v-if="!isNarrativeText(requisite.type) && isFieldVisible('requirementName')"
                        label="Name"
                        placeholder="Requirement Name"
                        class="pt-2"
                        :value="requisite.name"
                        :disabled="!isLevelEditable(level.key) || !isFieldEditable('requirementName')"
                        @input="updateData(requisite.id, 'name', $event)"
                      />
                      <FormText
                        v-if="!isNarrativeText(requisite.type) && isFieldVisible('sisId')"
                        label="SIS ID"
                        placeholder="Requirement SIS ID"
                        class="pt-2"
                        :value="requisite.sisId"
                        :disabled="!isLevelEditable(level.key) || !isFieldEditable('sisId')"
                        @input="updateData(requisite.id, 'sisId', $event)"
                      />
                      <div
                        v-for="(rule, ruleIndex) in requisite.rules || []"
                        :key="`rule-${ruleIndex}-${rule.id}`"
                        class="mb-2"
                      >
                        <SimpleRequisite
                          v-if="editing"
                          class="card card-body rounded"
                          :isProgram="isProgram"
                          :rule-data="rule"
                          :read-only="readOnly"
                          :editing="editingRuleId === rule.id"
                          :disabled="!isLevelEditable(level.key)"
                          :isRulesFieldVisible="isRulesFieldVisible"
                          :isRulesFieldEditable="isRulesFieldEditable"
                          :titleInterpolation="titleInterpolation"
                          @updatedRule="updateRule(requisite.id, ruleIndex, $event)"
                          @delete="deleteRule(requisite.id, ruleIndex)"
                        />
                      </div>
                      <AppButton
                        v-if="isRulesFieldVisible('addRule') && !isNarrativeText(requisite.type)"
                        variant="primary"
                        class="mt-2 mb-4 px-2 ml-1"
                        :raised="false"
                        :disabled="!isLevelEditable(level.key)"
                        @click="addRule(requisite.id)"
                      >
                        Add Requirement Rule
                      </AppButton>

                      <!-- The WYSIWYG editor has the iframe and file nodes disabled, because this content can causes unintended issues when displayed
                         in the requisite card in the public catalog-->
                      <FormWysiwyg
                        v-if="isFieldVisible('additionalComments')"
                        :value="requisite.notes"
                        :config="{
                          disableExtensions: ['embed', 'file']
                        }"
                        label="Additional Comments"
                        placeholder="Add any additional comments to this requisite..."
                        :disabled="!isLevelEditable(level.key) || !isFieldEditable('additionalComments')"
                        @input="updateData(requisite.id, 'notes', $event)"
                      />
                      <AppButton
                        v-if="isFieldVisible('deleteRequirement')"
                        variant="danger"
                        class="mt-2"
                        :raised="false"
                        :disabled="!isLevelEditable(level.key)"
                        dataTest="deleteRequisite"
                        @click="deleteRequisite(requisite.id)"
                      >
                        {{ `Delete ${isNarrativeText(requisite.type) ? 'Section' : 'Requirement'}` }}
                      </AppButton>
                    </template>
                  </div>
                </template>
              </CommonAccordion>
            </template>
          </AppSortable>
          <div
            v-for="(requisite, reqIndex) in localValue.filter(v => v.requirementLevel === level.key)"
            :key="reqIndex"
            class="mb-2"
          ></div>
        </div>
      </template>
      <template v-else>
        <AppSortable
          :value="localValue"
          :disabled="disabled"
          keySuffix="rq"
          @input="v => updateWithPositioning(v, useRequirementLevels)"
        >
          <template #default="{ item: requisite, key: reqIndex }">
            <CommonAccordion
              class="rounded-5 mb-2"
              style="background-color:#FCFCFC"
              :header-class="getHeaderClass(requisite.id)"
              :title="!disabled && 'Drag to sort'"
            >
              <template #header>
                <div class="d-flex w-100 p-3">
                  <AppIcon
                    v-if="!disabled"
                    name="drag_indicator"
                    class="mr-2"
                    style="opacity:0.5"
                  />
                  <span class="flex-1 text-left">
                    <span
                      :class="{
                        'diff-change-removed': removed && requisiteContainsChangedField(requisite.id, 'name'),
                        'diff-change-added': added && requisiteContainsChangedField(requisite.id, 'name'),
                      }"
                    >
                      {{ requisite.name || 'Untitled Requirement' }}
                    </span>
                  </span>
                  <span
                    :class="{
                      'diff-change-removed': removed && requisiteContainsChangedField(requisite.id, 'showInCatalog'),
                      'diff-change-added': added && requisiteContainsChangedField(requisite.id, 'showInCatalog'),
                    }"
                  >
                    <AppBadge v-if="requisite.showInCatalog">Shown in catalog</AppBadge>
                  </span>
                </div>
              </template>
              <template #content>
                <div
                  class="p-3"
                  :data-test="`simpleRequisiteContent${reqIndex}`"
                >
                  <template v-if="disabled">
                    <AppButtonWithLoader
                      v-if="isScribeGenerateEnabled"
                      class="float-right"
                      :raised="true"
                      :loading="isScribeGenerating"
                      @click="generateScribe(requisite)"
                    >Convert to Scribe</AppButtonWithLoader>
                    <div v-if="!isNarrativeText(requisite.type) && isFieldVisible('requirementName')">
                      <FormLabel label="Name" />
                      <span
                        :class="{
                          'diff-change-removed': removed && requisiteContainsChangedField(requisite.id, 'name'),
                          'diff-change-added': added && requisiteContainsChangedField(requisite.id, 'name'),
                        }"
                      >
                        {{ requisite.name || 'No Name Set' }}
                      </span>
                    </div>
                    <div v-if="!isNarrativeText(requisite.type) && isFieldVisible('sisId')">
                      <FormLabel label="SIS ID" />
                      <span
                        :class="{
                          'diff-change-removed': removed && requisiteContainsChangedField(requisite.id, 'sisId'),
                          'diff-change-added': added && requisiteContainsChangedField(requisite.id, 'sisId'),
                        }"
                      >
                        {{ requisite.sisId || 'No SIS ID Set' }}
                      </span>
                    </div>
                    <div v-if="!isNarrativeText(requisite.type)">
                      <FormLabel label="Type" />
                      <span
                        :class="{
                          'diff-change-removed': removed && requisiteContainsChangedField(requisite.id, 'type'),
                          'diff-change-added': added && requisiteContainsChangedField(requisite.id, 'type'),
                        }"
                      >
                        {{ (requisite.type || 'noTypeSet') | decamelize }}
                      </span>
                    </div>
                    <div
                      v-for="(rule, ruleIndex) in requisite.rules || []"
                      :key="`rule-${ruleIndex}-${rule.id}`"
                    >
                      <SimpleRequisiteRuleDisplay
                        :rule="rule"
                        :root="true"
                        :depth="0"
                        :removed="removed"
                        :added="added"
                        :changed-rule="changedRule(rule.id, requisite.id)"
                        :rule-diff="getRuleDiff(rule.id, requisite.id)"
                        :isRulesFieldVisible="isRulesFieldVisible"
                        :titleInterpolation="titleInterpolation"
                        class="card card-body"
                      />
                    </div>
                    <div v-if="isFieldVisible('additionalComments')">
                      <FormLabel
                        :id="`additionalComments${reqIndex}`"
                        label="Additional Comments"
                        class="pt-2"
                      />
                      <FormWysiwyg
                        v-if="requisite.notes"
                        :id="`additionalComments${reqIndex}`"
                        :disabled="true"
                        :value="requisite.notes"
                        :class="{
                          'diff-change-removed': removed && requisiteContainsChangedField(requisite.id, 'notes'),
                          'diff-change-added': added && requisiteContainsChangedField(requisite.id, 'notes'),
                        }"
                      />
                      <div
                        v-else-if="disabled"
                        class="text-muted"
                      >Not Set
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="d-flex w-100 mt-2">
                      <FormSelect
                        v-if="!isNarrativeText(requisite.type)"
                        label="Type"
                        placeholder="Requisite Type"
                        class="flex-1"
                        dataTest="requirementType"
                        :value="requisite.type"
                        :config="{options: filteredTypeOptions}"
                        @input="updateData(requisite.id, 'type', $event)"
                      />
                      <div v-if="isFieldVisible('showInCatalog')" class="flex-1 ml-4">
                        <FormLabel :id="`showInCatalog${reqIndex}`" label="Visibility" />
                        <AppSwitch
                          :id="`showInCatalog${reqIndex}`"
                          :disabled="!isFieldEditable('showInCatalog')"
                          :value="(requisite.showInCatalog === undefined)?showInCatalogDefaultValue:requisite.showInCatalog"
                          :label="`Show in ${$localize('catalog')}`"
                          class="pt-1"
                          @input="updateData(requisite.id, 'showInCatalog', $event)"
                        />
                      </div>
                    </div>
                    <FormText
                      v-if="!isNarrativeText(requisite.type) && isFieldVisible('requirementName')"
                      label="Name"
                      placeholder="Requirement Name"
                      class="pt-2"
                      :value="requisite.name"
                      @input="updateData(requisite.id, 'name', $event)"
                    />
                    <FormText
                      v-if="!isNarrativeText(requisite.type) && isFieldVisible('sisId')"
                      label="SIS ID"
                      placeholder="Requirement SIS ID"
                      class="pt-2"
                      :value="requisite.sisId"
                      @input="updateData(requisite.id, 'sisId', $event)"
                    />
                    <div
                      v-for="(rule, ruleIndex) in requisite.rules || []"
                      :key="`rule-${ruleIndex}-${rule.id}`"
                      class="mb-2"
                    >
                      <SimpleRequisite
                        v-if="editing"
                        class="card card-body rounded"
                        :rule-data="rule"
                        :read-only="readOnly"
                        :editing="editingRuleId === rule.id"
                        :isRulesFieldVisible="isRulesFieldVisible"
                        :isRulesFieldEditable="isRulesFieldEditable"
                        :titleInterpolation="titleInterpolation"
                        @updatedRule="updateRule(requisite.id, ruleIndex, $event)"
                        @delete="deleteRule(requisite.id, ruleIndex)"
                      />
                    </div>
                    <AppButton
                      v-if="isRulesFieldVisible('addRule') && !isNarrativeText(requisite.type)"
                      variant="primary"
                      class="mt-2 mb-4 px-2 ml-1"
                      :raised="false"
                      dataTest="addRequirementRule"
                      @click="addRule(requisite.id)"
                    >
                      Add Requirement Rule
                    </AppButton>

                    <!-- The WYSIWYG editor has the iframe and file nodes disabled, because this content can causes unintended issues when displayed
                       in the requisite card in the public catalog-->
                    <div>
                      <FormWysiwyg
                        v-if="isFieldVisible('additionalComments')"
                        :value="requisite.notes"
                        :config="{
                          disableExtensions: ['embed', 'file']
                        }"
                        :disabled="!isFieldEditable('additionalComments')"
                        label="Additional Comments"
                        placeholder="Add any additional comments to this requisite..."
                        @input="updateData(requisite.id, 'notes', $event)"
                      />
                    </div>
                    <AppButton
                      v-if="isFieldVisible('deleteRequirement')"
                      variant="danger"
                      class="mt-2"
                      :raised="false"
                      @click="deleteRequisite(requisite.id)"
                    >
                      {{ `Delete ${isNarrativeText(requisite.type) ? 'Section' : 'Requirement'}` }}
                    </AppButton>
                  </template>
                </div>
              </template>
            </CommonAccordion>
          </template>
        </AppSortable>
      </template>
    </template>
    <div
      v-if="!localValue.length"
      class="text-center text-muted bg-light rounded-5 p-3 mb-2 border"
    >
      No Requirements
    </div>
    <AppDropdown
      v-if="!disabled && isFieldVisible('addRequirement')"
      id="addNewSection"
      ref="dropdown"
      class="pt-2"
      name="Add"
      dataTest="addRequisite"
      buttonVariant="primary"
      buttonClass="btn-raised"
      appIconName="add"
    >
      <div class="border border-light shadow">
        <div class="hover-darken cursor-pointer p-2 d-flex align-items-center"
             @click="() => {
               addRequisite()
               $refs.dropdown.closeDropdown()
             }
             "
        >
          <AppIcon
            name="playlist_add_check_outlined"
            class="mr-1 flex-0"
            style="max-width: 20px"
            :size="16"
          />
          <span class="flex-1">Requirement</span>
        </div>
        <div class="hover-darken cursor-pointer p-2 d-flex align-items-center"
             @click="() => {
               addNarrativeText()
               $refs.dropdown.closeDropdown()
             }
             "
        >
          <AppIcon
            name="description_outlined"
            class="mr-1 flex-0"
            :size="16"
            style="max-width: 20px"
          />
          <span class="flex-1">Narrative Text</span>
        </div>
      </div>
    </AppDropdown>

    <ScribeSync
      v-if="modals.scribeGenerate && enableScribeEditor"
      :requisite="selectedRequisite"
      :scribeBlock="scribeBlock"
      :scribeErrors="scribeErrors"
      :sisRequisite="sisRequisite"
      :scribeValidations="scribeValidations"
      :school="school"
      :program="formValue"
      @close="modals.scribeGenerate = false"
    />

    <AppModal
      v-if="modals.scribeGenerate && !enableScribeEditor"
      @close="modals.scribeGenerate = false"
    >
      <template #modal-header>
        <AppHeading>Scribe Conversion Result</AppHeading>
      </template>
      <template #modal-body>
        <AppAlert
          v-for="(scribeError, index) in scribeErrorsTruncated"
          :key="`scribe-errors:${index}`"
          variant="warning"
          class="d-flex align-items-center mb-1"
        >
          <AppIcon name="warning" class="mr-2" />
          <span v-if="scribeError.errorCode === 'COURSE_NOT_FOUND'">
            Course with ID <strong>{{ scribeError.courseId }}</strong> could not be found.
          </span>
          <span v-else-if="scribeError.errorCode === 'COURSE_SET_NOT_FOUND'">
            Course set with ID <strong>{{ scribeError.courseSetId }}</strong> could not be found.
          </span>
          <span v-else-if="scribeError.errorCode === 'CONDITION_NOT_SUPPORTED'">
            Condition <strong>{{ scribeError.condition }}</strong> could not be converted to Scribe.
          </span>
        </AppAlert>

        <AppButton
          v-if="!showAllScribeErrors && scribeErrors.length > 1"
          variant="warning"
          class="btn-block"
          @click="showAllScribeErrors = true"
        >
          Show {{ scribeErrors.length - 1 }} more issues
        </AppButton>

        <pre style="white-space: pre-wrap">{{ scribeBlock }}</pre>
      </template>
      <template #modal-footer>
        <AppButton
          v-clipboard:copy="scribeBlock"
          v-clipboard:success="onCopySuccess"
        >
          <AppIcon name="content_copy" class="mr-2" />
          <span>Copy to Clipboard</span>
        </AppButton>
        <AppButton
          class="ml-auto"
          variant="secondary"
          @click="modals.scribeGenerate = false"
        >Close</AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import FormSelect from '@/components/Forms/FormSelect'
import FormText from '@/components/Forms/FormText'
import FormLabel from '@/components/Forms/FormLabel'
import SimpleRequisite from './SimpleRequisite.vue'
import FormMixin from '@/components/Forms/FormMixin'
import _clonedeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _uniq from 'lodash/uniq'
import _uniqBy from 'lodash/uniqBy'
import _sortBy from 'lodash/sortBy'
import { isEmpty } from '@shared/utils/isEmpty'
import CommonAccordion from '@/components/Common/CommonAccordion'
import SimpleRequisiteRuleDisplay from './SimpleRequisiteRuleDisplay'
import FormWysiwyg from '@/components/Forms/FormWysiwyg'
import { inject, provide, computed, ref } from '@vue/composition-api'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { CM_REQUISITES_SCRIBE_GENERATE, CM_REQUIREMENT_LEVELS, CM_SCRIBE_EDITOR } from '@shared/constants/featureFlags'
import { postScribeBlockExport, getIntegrationData } from '@/api'
import { mapGetters, mapState } from 'vuex'
import useDocumentTemplate from '@/components/composables/useDocumentTemplate'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'
import { camelize } from '@shared/utils/string'
import { SHOW_IN_CATALOG_DEFAULT_VALUE } from '@shared/constants/requisites'
import ScribeSync from '@cm/client/components/Requisites/ScribeSync.vue'
import { SCRIBE_VALIDATIONS } from '@shared/integration/scribeConstants'

export default {
  mixins: [ FormMixin ],
  components: {
    FormSelect,
    FormText,
    FormLabel,
    SimpleRequisite,
    CommonAccordion,
    SimpleRequisiteRuleDisplay,
    FormWysiwyg,
    ScribeSync,
  },
  props: {
    iteration: {
      type: Number,
      default: 0,
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    formId: {
      type: String,
      default: '',
    },
    formType: {
      type: String,
      default: '',
    },
    changedRequisiteIds: {
      type: Array,
      default: () => ([]),
    },
    changedRequisitesArray: {
      type: Array,
      default: () => ([]),
    },
    unchangedIdsWithPositions: {
      type: Array,
      default: () => ([]),
    },
    added: {
      type: Boolean,
      default: false,
    },
    removed: {
      type: Boolean,
      default: false,
    },
    diffEnabled: {
      type: Boolean,
      default: false,
    },
    disableRequirementLevels: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      typeOptions: [
        'Prerequisite',
        'Corequisite',
        'Antirequisite',
        'Completion Requirement',
      ],
      localValue: [],
      // default the requisite is diisplayed in readonly,until user clicks "edit" button
      editing: true,
      editingRuleId: null,
      modals: {
        scribeGenerate: false,
      },
      isScribeGenerating: false,
      scribeBlock: null,
      scribeValidations: {},
      scribeValidationsReady: false,
      scribeErrors: [],
      showAllScribeErrors: false,
      sisRequisite: {},
      selectedRequisite: undefined,
    }
  },
  setup (props, { root }) {
    provide('formValue', props.formValue)

    let formUpdate = () => {}
    if (props.formId) {
      formUpdate = inject(`${props.formId}_update`)
    }

    const { enableScribeEditor, isScribeGenerateEnabled, isRequirementLevelsFeatureFlagEnabled } = useFeatureFlags({
      isScribeGenerateEnabled: CM_REQUISITES_SCRIBE_GENERATE,
      isRequirementLevelsFeatureFlagEnabled: CM_REQUIREMENT_LEVELS,
      enableScribeEditor: CM_SCRIBE_EDITOR,
    })

    const revisionChangeIteration = inject('revisionChangeIteration', 0)

    const isProgram = props.formId === 'program-form' || props.formType === 'program'

    const isRequirementLevelsEnabled = isRequirementLevelsFeatureFlagEnabled === true

    const { baseQuestionsArray, loadBaseTemplate, isLoading: isLoadingTemplate } =
      useDocumentTemplate({ type: ref(isProgram ? 'program' : 'course') })
    const requirementsTemplateObject = computed(() => baseQuestionsArray.value.find(question => question.dataKey === 'requisites'))
    const requirementLevelsTemplate = computed(() => _get(requirementsTemplateObject.value, 'config.fields.requisitesSimple.config.fields.requirementLevels.config.options', []))

    const currentLevelsKeys = _uniq(props.formValue.requirementLevels?.map(level => level.key) || [])

    const requirementLevels = computed(() =>
      props.formValue.requirementLevels?.length > 0
        ? _uniqBy([ ...props.formValue.requirementLevels, ...requirementLevelsTemplate.value.filter(level => !currentLevelsKeys.includes(level.key)) ], 'key')
        : requirementLevelsTemplate.value)

    const user = root.$store.getters.user
    const viewableRequirementLevels = computed(() => requirementLevelsTemplate.value.filter(
      level => !level.rolesAllowedToSee || userHasAnyAllowedRole(user, null, level.rolesAllowedToSee)
    ))
    const editableRequirementLevelsKeys = computed(() => requirementLevelsTemplate.value.filter(
      level => !level.rolesAllowedToEdit || userHasAnyAllowedRole(user, null, level.rolesAllowedToEdit)
    ).map(level => level.key))

    const requirementLevelsTemplateOptions = computed(() =>
      viewableRequirementLevels
        .value
        .filter(level => editableRequirementLevelsKeys.value.includes(level.key))
        .map(level => ({ key: level.key, label: level.label }))
    )
    const specializationsOptions = computed(() => props.formValue.specializations?.map(item => item.id || item.code) || [])
    const isLoading = computed(() => isLoadingTemplate.value)

    return {
      isScribeGenerateEnabled,
      isProgram,
      isRequirementLevelsEnabled,
      revisionChangeIteration,
      baseQuestionsArray,
      loadBaseTemplate,
      isLoading,
      requirementLevels,
      requirementTemplateConfigurations: requirementsTemplateObject,
      requirementLevelsTemplate,
      currentLevelsKeys,
      viewableRequirementLevels,
      editableRequirementLevelsKeys,
      requirementLevelsTemplateOptions,
      specializationsOptions,
      formUpdate,
      enableScribeEditor,
    }
  },
  computed: {
    ...mapGetters([ 'school', 'user' ]),
    ...mapState([ 'requests' ]),
    defaultRule () {
      return {
        condition: 'completedAllOf',
        value: undefined,
        subRules: undefined,
        number: undefined,
        gradeType: undefined,
        grade: undefined,
        credits: undefined,
      }
    },
    scribeErrorsTruncated () {
      return this.showAllScribeErrors ? this.scribeErrors : this.scribeErrors.slice(0, 1)
    },
    requirementsWithoutLevels () {
      const levelKeys = this.requirementLevelsTemplate.map(level => level.key)
      return this.localValue.filter((v, i) => {
        if (v.requirementLevel && !levelKeys.includes(v.requirementLevel)) {
          this.localValue[i].requirementLevel = undefined
        }
        return !levelKeys.includes(v.requirementLevel)
      })
    },
    requirementFieldsConfiguration () {
      const requisitesSimpleConfig = this?.config?.fields?.requisitesSimple || {}
      return requisitesSimpleConfig?.config?.fields || {}
    },
    noLevelObject () {
      return {
        label: '',
        key: undefined,
        notes: '',
        config: {
          editorEnabled: false,
        },
      }
    },
    useRequirementLevels () {
      return !this.disableRequirementLevels && this.isRequirementLevelsEnabled && this.viewableRequirementLevels.length
    },
    showInCatalogDefaultValue () {
      if (!this.requirementFieldsConfiguration) return SHOW_IN_CATALOG_DEFAULT_VALUE

      const fieldConfig = this.requirementFieldsConfiguration['showInCatalog'] || {}

      if (!fieldConfig.config) { return SHOW_IN_CATALOG_DEFAULT_VALUE }

      return fieldConfig.config.defaultValue
    },
    titleInterpolation () {
      return this?.config?.fields?.requisitesSimple?.titleInterpolation ||
             this?.requirementTemplateConfigurations?.config?.fields?.requisitesSimple?.titleInterpolation ||
             ''
    },
    filteredTypeOptions () {
      const optionsConfig = (this.requirementFieldsConfiguration['requirementType']?.config.options || {})

      return this.typeOptions.filter(option => userHasAnyAllowedRole(this.user, null, optionsConfig[camelize(option)]?.rolesAllowedToSee || null))
    },
    entityType () {
      if (this.formType === 'course' || this.formId === 'course-form') {
        return 'course'
      } else if (this.formType === 'program' || this.formId === 'program-form') {
        return 'program'
      }
      return undefined
    },
  },
  watch: {
    iteration: {
      handler: 'updateLocalValue',
      immediate: true,
    },
    revisionChangeIteration: {
      handler: 'updateLocalValue',
    },
    value: {
      handler: 'updateLocalValue',
    },
    requirementLevels: {
      handler: 'updateCurrentRequirementLevelsKeys',
    },
  },
  async created () {
    if (this.isRequirementLevelsEnabled && isEmpty(this.baseQuestionsArray) && this.loadBaseTemplate) {
      await this.loadBaseTemplate()
    }
    if (this.localValue.length) {
      this.localValue = _sortBy(this.localValue, 'position')
      this.emitData()
    }
    if (this.enableScribeEditor && this.isProgram) {
      await this.getScribeValidations()
    }
  },
  methods: {
    isFieldVisible (fieldKey) {
      if (!this.requirementFieldsConfiguration) return false

      const fieldConfig = (this.requirementFieldsConfiguration[fieldKey] || {})['rolesAllowedToSee']
      return userHasAnyAllowedRole(this.user, null, fieldConfig)
    },
    isFieldEditable (fieldKey) {
      if (!this.requirementFieldsConfiguration) return false

      const fieldConfig = (this.requirementFieldsConfiguration[fieldKey] || {})['rolesAllowedToEdit']
      return userHasAnyAllowedRole(this.user, null, fieldConfig)
    },
    isRulesFieldVisible (fieldKey) {
      if (!this.requirementFieldsConfiguration) return false

      const rulesFieldConfig = (this.requirementFieldsConfiguration?.rules?.config.fields || {})
      const fieldConfig = (rulesFieldConfig[fieldKey] || {})['rolesAllowedToSee']
      return userHasAnyAllowedRole(this.user, null, fieldConfig)
    },
    isRulesFieldEditable (fieldKey) {
      if (!this.requirementFieldsConfiguration) return false

      const rulesFieldConfig = (this.requirementFieldsConfiguration?.rules?.config.fields || {})
      const fieldConfig = (rulesFieldConfig[fieldKey] || {})['rolesAllowedToEdit']

      return userHasAnyAllowedRole(this.user, null, fieldConfig)
    },
    updateLocalValue () {
      this.localValue = _clonedeep(this.value || [])
    },
    updateCurrentRequirementLevelsKeys () {
      this.currentLevelsKeys = _uniq(this.requirementLevels?.map(level => level.key) || [])
    },
    getRequirementLevelLabel (key) {
      return this.requirementLevelsTemplate.find(level => level.key === key)?.label || '(No requirement level)'
    },
    getRequirementLevelNote (key) {
      return this.requirementLevels.find(level => level.key === key)?.notes || ''
    },
    emitData () {
      this.$emit('input', this.localValue)
    },
    updateData (id, key, val) {
      const index = this.localValue.findIndex(v => v.id === id)
      this.$set(this.localValue[index], key, val)
      this.emitData()
    },
    updateRequirementLevelsData (key, field, val) {
      const value = _clonedeep(this.requirementLevels || [])
      const index = value.findIndex(level => level.key === key)
      if (index > -1) {
        value[index][field] = val
        this.formUpdate({ property: 'requirementLevels', value })
      }
    },
    addRule (requirementId) {
      const index = this.localValue.findIndex(v => v.id === requirementId)
      const requisite = this.localValue[index]
      if (!requisite.rules) this.$set(requisite, 'rules', [])
      const id = this.generateToken(8)
      const newRule = {
        id,
        ...this.defaultRule,
      }
      this.editingRuleId = id
      requisite.rules.push(newRule)
      this.emitData()
    },
    updateRule (id, ruleIndex, rule) {
      const index = this.localValue.findIndex(v => v.id === id)
      const requisite = this.localValue[index]
      if (!requisite.rules) this.$set(requisite, 'rules', [])
      this.$set(requisite.rules, ruleIndex, rule)
      this.emitData()
    },
    deleteRule (id, ruleIndex) {
      const index = this.localValue.findIndex(v => v.id === id)
      const requisite = this.localValue[index] || {}
      if (!requisite.rules) return
      if (!requisite.rules[ruleIndex]) return
      this.$delete(requisite.rules, ruleIndex)
      this.emitData()
    },
    addRequisite () {
      if (this.filteredTypeOptions.includes('Completion Requirement')) {
        this.localValue.push({
          id: this.generateToken(8), name: 'Untitled Requirement', type: 'Completion Requirement', condition: 'allOf', showInCatalog: this.showInCatalogDefaultValue,
        })
      } else {
        this.localValue.push({
          id: this.generateToken(8), name: 'Untitled Requirement', type: this.filteredTypeOptions[0] || null, condition: 'allOf', showInCatalog: this.showInCatalogDefaultValue,
        })
      }
      this.emitData()
    },
    addNarrativeText () {
      this.localValue.push({
        id: this.generateToken(8), name: 'Narrative Text', type: 'Narrative Text', showInCatalog: this.showInCatalogDefaultValue,
      })
      this.emitData()
    },
    deleteRequisite (id) {
      const index = this.localValue.findIndex(v => v.id === id)
      this.localValue.splice(index, 1)
      this.emitData()
    },
    async getSisRequisite (requisite) {
      if (!requisite.sisId) return
      const { formattedData } = await getIntegrationData({
        school: this.school,
        type: `${this.entityType}Requisites`,
        objectId: requisite.sisId,
      })
      return formattedData[requisite.sisId]
    },
    async getScribeValidation (validationId) {
      const response = await getIntegrationData({
        school: this.school,
        type: `scribeValidations`,
        validationId,
      })
      return response?.formattedData || []
    },
    async getScribeValidations () {
      const validations = []
      const validatorIds = Object.values(SCRIBE_VALIDATIONS)
      for (const validationId of validatorIds) {
        validations.push(await this.getScribeValidation(validationId))
      }
      this.scribeValidations = validatorIds.reduce((accumulator, key, index) => ({
        ...accumulator,
        [key]: validations[index],
      }), {})
      this.scribeValidationsReady = true
    },
    async waitForScribeValidations (requisite) {
      if (this.scribeValidationsReady) return
      await new Promise(resolve => setTimeout(resolve, 500))
      await this.waitForScribeValidations(requisite)
    },
    async generateScribe (requisite) {
      this.isScribeGenerating = true
      const [ { scribeBlock, errors }, sisRequisite ] = await Promise.all([
        postScribeBlockExport({
          school: this.school,
          entityId: this.formValue.id,
          entityType: this.entityType,
          requisiteId: requisite.id,
        }),
        this.getSisRequisite(requisite),
      ])
      this.sisRequisite = sisRequisite
      this.scribeBlock = scribeBlock
      this.selectedRequisite = requisite
      if (!sisRequisite) this.scribeErrors.push({ errorCode: 'SCRIBE_NOT_FOUND' })
      await this.waitForScribeValidations(requisite)
      this.isScribeGenerating = false
      this.modals.scribeGenerate = true
    },
    onCopySuccess () {
      this.$store.dispatch('ui/showSnackbar', { text: 'Scribe copied to clipboard.', variant: 'info' })
    },
    isLevelEditable (key) {
      return this.editableRequirementLevelsKeys.includes(key) || !key
    },
    requisiteContainsChangedField (id, fieldName) {
      const requisiteObject = this.changedRequisitesArray.find(r => r.id === id)
      if (requisiteObject) {
        return requisiteObject.fields.includes(fieldName)
      } else return false
    },
    changedRule (ruleId, requisiteId) {
      const requisiteObject = this.changedRequisitesArray.find(r => r.id === requisiteId)
      if (requisiteObject) {
        return !requisiteObject.rules.map(r => r.id).includes(ruleId)
      }
      return false
    },
    getRuleDiff (ruleId, requisiteId) {
      const requisiteObject = this.changedRequisitesArray.find(r => r.id === requisiteId)
      if (requisiteObject) {
        return requisiteObject.rules.find(r => r.id === ruleId)
      }
      return undefined
    },
    ruleContainsChangedValueField (requisiteId, ruleId) {
      const requisiteObject = this.addedRequisitesArray.find(r => r.id === requisiteId)
      if (requisiteObject) {
        const rule = requisiteObject.rules.find(r => r.id === ruleId)
        if (rule) {
          return rule.fields
        }
      }
      return false
    },
    getHeaderClass (id) {
      const unchangedIdObject = this.unchangedIdsWithPositions.find(
        r => r.id === id
      )
      if (
        this.changedRequisiteIds.includes(id) ||
        (unchangedIdObject &&
         unchangedIdObject.oldPosition !== unchangedIdObject.newPosition)
      ) {
        if (this.added) {
          return 'diff-change-added'
        } else if (this.removed) {
          return 'diff-change-removed'
        }
      }
      return ''
    },
    updateWithPositioning (values, useRequirementLevels) {
      if (useRequirementLevels) {
        const sortArray = values.map(v => this.localValue.findIndex(lv => lv.id === v.id)).sort()
        sortArray.forEach((e, i) => {
          this.localValue[e] = values[i]
          this.localValue[e].position = i
        })
      } else {
        this.localValue = values.map((v, i) => ({ ...v, position: i }))
      }
      this.emitData()
      return this.localValue
    },
    isNarrativeText (type) {
      return type === 'Narrative Text'
    },
  },
}
</script>

<style scoped>
  .diff-change-removed {
    background: #fccac7 !important;
    color: #7f231c;
  }

  .diff-change-added {
    background: #cde9ce !important;
    color: #285b2a;
  }

  .hover-darken {
    transition: background 0.3s ease;
  }

  .hover-darken:hover {
    background: rgba(0, 0, 0, 0.05);
  }
</style>
